import React, { useRef, useState, useCallback } from 'react';
import ItemRow from './ItemRow';

function ItemsSection({
  offerWithCalculations,
  offer,
  tableRef,
  effectiveWidths,
  handleMouseDown,
  handleItemChange,
  deleteRow,
  toggleHiddenRow,
  moveRow,
  addRowToTop,
  addRowToBottom,
  flushPendingUpdate,
  products,
  vendors,
  showProductDropdown,
  showVendorDropdown,
  searchingProduct,
  searchingVendor,
  setShowProductDropdown,
  setShowVendorDropdown,
  filterProducts,
  filterVendors,
  fetchAllProducts,
  fetchVendorsForProduct,
  selectProduct,
  selectVendor,
  pendingSupplierQueue,
  advanceSupplierQueue,
  setActiveColumn,
  activeColumn,
  formatNumber,
  isReadOnly
}) {
  const { items } = offerWithCalculations;

  // Drag-and-drop state
  const dragIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = useCallback((e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image semi-transparent
    if (e.currentTarget.closest('tr')) {
      e.dataTransfer.setDragImage(e.currentTarget.closest('tr'), 0, 0);
    }
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((e, index) => {
    e.preventDefault();
    const fromIndex = dragIndexRef.current;
    if (fromIndex !== null && fromIndex !== index) {
      moveRow(fromIndex, index);
    }
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }, [moveRow]);

  const handleDragEnd = useCallback(() => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }, []);

  const columns = ['Drag', '#', 'Product', 'Description', 'Supplier', 'Units', 'UnitCost', 'TotalCost', 'Markup(%)', 'UnitPrice', 'TotalPrice', 'LocalCost', 'CostCur', 'Profit', 'Profit(%)', 'VAT', 'VAT Price', 'TotalPrice+VAT', 'Action', 'Hidden'];
  const noHeaderCols = new Set(['Action', 'Hidden', 'Drag']);
  const noResizeCols = new Set(['Action', 'Hidden', '#', 'Drag']);

  return (
    <div className="w-full" onBlur={() => flushPendingUpdate()}>
      <div className="bg-white">
        <table ref={tableRef} className="w-full table-fixed border-collapse text-xs">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="relative whitespace-nowrap bg-white px-2 py-2 text-left text-[12px] font-medium tracking-wide text-black"
                  style={{ width: effectiveWidths[col] + 'px', minWidth: effectiveWidths[col] + 'px', transition: 'width 0.15s ease' }}
                >
                  {noHeaderCols.has(col) ? '' : col}
                  {!noResizeCols.has(col) && (
                    <div
                      className="absolute right-0 top-2 bottom-2 w-0.5 cursor-col-resize rounded-full bg-black/20 transition hover:bg-company-main/60"
                      onMouseDown={(e) => handleMouseDown(e, col)}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <ItemRow
                key={item.id}
                index={index}
                item={item}
                items={items}
                handleItemChange={handleItemChange}
                deleteRow={deleteRow}
                toggleHiddenRow={toggleHiddenRow}
                filterProducts={filterProducts}
                filterVendors={filterVendors}
                products={products}
                vendors={vendors}
                showProductDropdown={showProductDropdown}
                setShowProductDropdown={setShowProductDropdown}
                showVendorDropdown={showVendorDropdown}
                setShowVendorDropdown={setShowVendorDropdown}
                searchingProduct={searchingProduct}
                searchingVendor={searchingVendor}
                selectProduct={selectProduct}
                selectVendor={selectVendor}
                pendingSupplierQueue={pendingSupplierQueue}
                advanceSupplierQueue={advanceSupplierQueue}
                fetchAllProducts={fetchAllProducts}
                fetchVendorsForProduct={fetchVendorsForProduct}
                setActiveColumn={setActiveColumn}
                isReadOnly={isReadOnly}
                dragOverIndex={dragOverIndex}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            ))}
            {/* Sentinel row: drop target for placing an item after the last row */}
            <tr
              className={`drag-sentinel${dragOverIndex === items.length ? ' drag-over-row' : ''}`}
              onDragOver={(e) => handleDragOver(e, items.length)}
              onDrop={(e) => handleDrop(e, items.length)}
            >
              <td colSpan={20} />
            </tr>
          </tbody>
        </table>
        <div className="add-row-btns">
          <button onClick={addRowToTop} className="add-row-btn shadow-lg" disabled={isReadOnly}>+ Add Row to Top</button>
          <button onClick={addRowToBottom} className="add-row-btn shadow-lg" disabled={isReadOnly}>+ Add Row to Bottom</button>
        </div>
      </div>
    </div>
  );
}

export default ItemsSection;
